import React, { Component } from "react";
import PropTypes from "prop-types";
import { Table, Form, Button, Grid, Segment } from "semantic-ui-react";
import { withRouter } from "react-router-dom";
import { read, write } from "xlsx";
import Constants from "../utilities/Constants";
import Strings from "../utilities/StringSelector";
import Utils from "../utilities/Utils";

const ipRegex = new RegExp("^[0-2][0-9.]*$");
const nameRegex = new RegExp("^[A-Za-z][A-Za-z-]*");

const UtilsInstance = new Utils();

class Topology extends Component {

  // Intialize this component state
  constructor(props) {
    super(props);
    this.state = {
      formInput: "",
      column: null,
      sortDirection: null,
      originalDevices: [],
      devices: [],
    };
  }

  // Creates a list with entries that match the IP passed on the form
  handleIPSearch = () => {
    const len = this.state.originalDevices.length;
    const searchedDevices = [];

    for (let i = 0; i < len; i++) {
      if (this.state.originalDevices[i].ip === this.state.formInput) {
        searchedDevices.push(this.state.originalDevices[i]);
      }
    }

    if (searchedDevices.length > 0) {
      this.setState({ devices: searchedDevices });
    } else {
      this.setState({ devices: [] });
    }
  };

  // Creates a list with entries that match the name passed on the form
  handleNameAndModelSearch = () => {
    const len = this.state.originalDevices.length;
    const searchedDevices = [];

    for (let i = 0; i < len; i++) {
      // Uppercases both strings before comparison to allow search flexibility
      // Also remove all whitespaces in both strings before comparing
      if (this.state.originalDevices[i].name != null && this.state.formInput != null) {

        let noWhitespaceUppercasedDeviceName = this.state.originalDevices[i].name.toUpperCase().replace(/\s/g, "");
        let noWhitespaceUppercasedDeviceModel = this.state.originalDevices[i].model.toUpperCase().replace(/\s/g, "");
        let noWhitespaceUppercasedFormInput = this.state.formInput.toUpperCase().replace(/\s/g, "");

        if (noWhitespaceUppercasedDeviceName.indexOf(noWhitespaceUppercasedFormInput) !== -1) {
          searchedDevices.push(this.state.originalDevices[i]);
        }
        else if (noWhitespaceUppercasedDeviceModel.indexOf(noWhitespaceUppercasedFormInput) !== -1) {
          searchedDevices.push(this.state.originalDevices[i]);
        }
      }
    }

    if (searchedDevices.length > 0) {
      this.setState({ devices: searchedDevices });
    } else {
      this.setState({ devices: [] });
    }
  };

  /*
    Filters the page list showing only the devices that match the
    ip or name the user has typed
  */
  handleSubmit = () => {
    if (this.state.formInput !== "" && ipRegex.test(this.state.formInput)) {
      this.handleIPSearch();
    }
    else if (this.state.formInput !== "" && nameRegex.test(this.state.formInput)) {
      this.handleNameAndModelSearch();
    }
    else if (this.state.formInput === "") {
      this.setState({ devices: this.state.originalDevices });
    }
  };

  retrieveData = () => {
    if (this.state.formInput === "") {
      // Retrieve devices list
      fetch(Constants.REQUEST_IP_ADDRESS + "topology", { mode: "cors" })
        .then(res => res.json())
        .then((data) => {
          let devices = this.adjustDevicesData(data);

          this.setState({
            devices: devices,
            originalDevices: devices
          });
        })
        .catch((error) => {
          console.log(error);
          this.setState({
            devices: [],
            originalDevices: []
          });
        });
    }
  };

  adjustDevicesData = (devices) => {
    let devicesCopy = JSON.parse(JSON.stringify(devices));

    for (let i = 0; i < devicesCopy.length; i++) {
      if (devicesCopy[i].model === null) {
        devicesCopy[i].model = "None";
      }

      if (devicesCopy[i].name === null) {
        devicesCopy[i].name = "None";
      }

      // Then sort their ports alphabetically
      devicesCopy[i].connections.sort(
        UtilsInstance.sortBy(
          "selfPort",
          false,
          function (a) { return UtilsInstance.treatUppercase(a); }
        )
      );
    }

    devicesCopy.sort((a, b) => {
      const num1 = Number(a.ip.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
      const num2 = Number(b.ip.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
      return num1 - num2;
    });

    return devicesCopy;
  };

  /*
    Adjusts the size of the table so that it does not excced the
    browser window height limit
  */
  adjustTableMaxHeight = () => {
    const browserHeight = UtilsInstance.getBrowserWindowHeight();
    const tableContainer = document.getElementById("main-table-container");

    if (tableContainer) {
      const newTableMaxHeight = Math.max(browserHeight - 285, 280);
      tableContainer.style.maxHeight = `${newTableMaxHeight}px`;
    }
  };

  /*
    Saves the form input to the component state everytime the user
    types something in the form
  */
  handleChange = (e, { name, value }) => this.setState({ [name]: value });

  getNameTextForCSV = (name) => {
    if (name === undefined || name === null || name === "" || name === "None") {
      return Strings.NO_ANSWER;
    }
    else {
      return name.replace(/,/g, " ");
    }
  };

  exportTopologyAsCSV = () => {
    let csvText = Strings.TOPOLOGY_CSV_HEADER;

    for (let i = 0; i < this.state.originalDevices.length; i++) {

      if (this.state.originalDevices[i].connections.length === 0) {
        csvText += this.state.originalDevices[i].snmp + ","
          + this.getNameTextForCSV(this.state.originalDevices[i].name) + ","
          + this.getNameTextForCSV(this.state.originalDevices[i].model) + ","
          + this.getNameTextForCSV(this.state.originalDevices[i].ip) + ","
          + ",,,\n";
      }
      else {
        for (let j = 0; j < this.state.originalDevices[i].connections.length; j++) {
          csvText += this.state.originalDevices[i].snmp + ","
            + this.getNameTextForCSV(this.state.originalDevices[i].name) + ","
            + this.getNameTextForCSV(this.state.originalDevices[i].model) + ","
            + this.getNameTextForCSV(this.state.originalDevices[i].ip) + ","
            + this.getNameTextForCSV(this.state.originalDevices[i].connections[j].selfPort) + ","
            + this.getNameTextForCSV(this.state.originalDevices[i].connections[j].neighborName) + ","
            + this.getNameTextForCSV(this.state.originalDevices[i].connections[j].neighborPort) + ","
            + this.getNameTextForCSV(this.state.originalDevices[i].connections[j].neighborIP) + "\n";
        }
      }
    }

    const workbook = read(csvText, { type: "string", raw: true });
    /* Aqui convertemos o workbook para o formato .xlsx */
    const wbout = write(workbook, { bookType: "xlsx", type: "binary" });

    /* Criação do blob e link para download */
    const blob = new Blob([UtilsInstance.s2ab(wbout)], { type: "application/octet-stream" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = Strings.TOPOLOGY_CSV_EXPORT_NAME;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ----------------- Lifecycle Methods ----------------
  componentDidMount = () => {
    sessionStorage.removeItem("auth");
    this.retrieveData();
    // store intervalId in the state so it can be accessed later
    this.setState({ intervalID: setInterval(this.retrieveData, 5000) });
    this.adjustTableMaxHeight();
  };

  componentWillUnmount = () => {
    clearInterval(this.state.intervalID);
  };

  componentDidUpdate = () => {
    this.adjustTableMaxHeight();
  };

  openDeviceInGraphicTopology = (deviceIP) => {
    this.props.onOpenGraphicTopology({
      deviceIP,
      source: "topology-list"
    });
  };

  handleDeviceRowKeyDown = (event, deviceIP) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.openDeviceInGraphicTopology(deviceIP);
    }
  };

  // Render the table containing the network topology
  render() {
    const filter = this.state.formInput;

    const topologyDevices = this.state.devices.map((device, index) => {
      return (
        <Table.Body key={`${device.ip}-${index}`}>
          <Table.Row
            className="ts-topology-device-row"
            style={mainTableRowStyle}
            tabIndex="0"
            title={Strings.OPEN_DEVICE_IN_GRAPHIC_TOPOLOGY}
            onClick={() => this.openDeviceInGraphicTopology(device.ip)}
            onKeyDown={(event) => this.handleDeviceRowKeyDown(event, device.ip)}>
            <Table.Cell>{device.snmp}</Table.Cell>
            <Table.Cell>{UtilsInstance.getDeviceName(device.name, Strings, UtilsInstance)}</Table.Cell>
            <Table.Cell>{UtilsInstance.getDeviceName(device.model, Strings, UtilsInstance)}</Table.Cell>
            <Table.Cell>{device.ip}</Table.Cell>
            <Table.Cell colSpan="4" />
          </Table.Row>
          {device.connections.map(connection => (
            <Table.Row key={connection.selfPort}>
              <Table.Cell colSpan="4" />
              <Table.Cell>{connection.selfPort}</Table.Cell>
              <Table.Cell>{UtilsInstance.getDeviceName(connection.neighborName, Strings, UtilsInstance)}</Table.Cell>
              <Table.Cell>{connection.neighborPort}</Table.Cell>
              <Table.Cell>{UtilsInstance.getDeviceIP(connection.neighborIP, Strings)}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      );
    });

    return (
      <div style={screenStyle}>

        <Grid className="ts-topology-list-toolbar" columns="equal" textAlign="center" verticalAlign="middle">
          <Grid.Row>
            <Grid.Column>

            </Grid.Column>

            <Grid.Column>
              <Form style={searchStyle} onSubmit={this.handleSubmit} >
                <Form.Input
                  id="inputform"
                  placeholder={Strings.TOPOLOGY_INPUT_TEXT_PLACEHOLDER}
                  icon="search"
                  name="formInput"
                  value={filter}
                  onChange={this.handleChange} />
              </Form>
            </Grid.Column>

            <Grid.Column>
              <Segment id="legend-segment" basic textAlign="right">
                <Button
                  content={Strings.EXPORT_CSV}
                  color="teal"
                  onClick={this.exportTopologyAsCSV} />
              </Segment>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Table id="table-header" style={headerMarginStyle} compact stackable fixed celled textAlign="center">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{Strings.SNMP}</Table.HeaderCell>
              <Table.HeaderCell>{Strings.NAME}</Table.HeaderCell>
              <Table.HeaderCell>{Strings.MODEL}</Table.HeaderCell>
              <Table.HeaderCell>{Strings.IP}</Table.HeaderCell>
              <Table.HeaderCell>{Strings.PORT}</Table.HeaderCell>
              <Table.HeaderCell>{Strings.NEIGHBOUR_NAME}</Table.HeaderCell>
              <Table.HeaderCell>{Strings.NEIGHBOUR_PORT_1} {Strings.NEIGHBOUR_PORT_2}</Table.HeaderCell>
              <Table.HeaderCell>{Strings.NEIGHBOUR_IP}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
        </Table>

        <div id="main-table-container" style={tableStyle}>
          <Table id="main-table" compact stackable fixed celled textAlign="center">
            {topologyDevices}
          </Table>
        </div>
      </div>
    );
  }
}

// CSS custom table stylization
const screenStyle = {
  marginTop: "8px",
  marginLeft: "4px",
  marginRight: "4px",
  textAlign: "center",
};

const tableStyle = {
  overflowY: "scroll"
};

const headerMarginStyle = {
  marginTop: "0px",
  marginBottom: "8px",
  width: "99%"
};

const mainTableRowStyle = {
  backgroundColor: "rgba(0, 0, 50, 0.04)"
};

const searchStyle = {
  margin: "0 auto",
  marginBottom: "16px"
};

Topology.propTypes = {
  onOpenGraphicTopology: PropTypes.func.isRequired
};

export default withRouter(Topology);